import { Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import classNames from "classnames";
import { useState } from "react";
import css from "./ReductionField.module.scss";

const REDUCTION_STEP = 5;

const roundTo = (value: number, decimals: number): number =>
    Math.round(value * 10 ** decimals) / 10 ** decimals;

export const ReductionField: React.FC<{
    readonly value: number;
    readonly onChange: (value: number) => void;
    readonly ariaLabel: string;
    readonly max?: number;
    /** Shown after the value; an empty string shows none. */
    readonly unit?: string;
    /** Decimal places the field shows and accepts. */
    readonly decimals?: number;
    /** Whether to highlight the value as changed; defaults to any value above 0. */
    readonly isSet?: boolean;
    /** Whether clearing the input commits 0; when false, the cleared text is a draft until blur restores the value. */
    readonly commitEmpty?: boolean;
    /** Arrow increment. With `readOnly`, the arrows are the only way to change the value. */
    readonly stepSize?: number;
    readonly readOnly?: boolean;
    /** Show the value without any way to change it, keeping the steppers' space so columns stay aligned. */
    readonly disabled?: boolean;
}> = ({
    value,
    onChange,
    ariaLabel,
    max = 100,
    unit = "%",
    decimals = 1,
    isSet = value > 0,
    commitEmpty = true,
    stepSize = REDUCTION_STEP,
    readOnly = false,
    disabled = false
}) => {
    const [draft, setDraft] = useState<string>();
    const typedText = new RegExp(`^\\d*(\\.\\d{0,${decimals}})?$`);
    // A full MW size like 21.609 shows as 21.61; reaching that rounded max saves
    // the exact max, so retyping it restores the size instead of a tiny reduction.
    const roundedMax = roundTo(max, decimals);
    const setValue = (next: number) =>
        onChange(
            roundTo(next, decimals) >= roundedMax
                ? max
                : Math.max(0, roundTo(next, decimals) || 0)
        );
    const text = draft ?? (value > 0 ? String(roundTo(value, decimals)) : "");
    const step = (delta: number) => {
        setDraft(undefined);
        setValue(value + delta);
    };

    return (
        <div className={css["ReductionField"]}>
            <span className={css["ReductionField-input"]}>
                <input
                    size={text.length || 1}
                    className={classNames(
                        css["ReductionField-value"],
                        isSet && css["ReductionField-value--set"]
                    )}
                    value={text}
                    placeholder="0"
                    aria-label={ariaLabel}
                    readOnly={readOnly || disabled}
                    onChange={(event) => {
                        const next = event.target.value;
                        const typed = Number(next);
                        if (next === "" && !commitEmpty) {
                            setDraft("");
                            return;
                        }
                        if (typedText.test(next) && typed <= roundedMax) {
                            setDraft(next);
                            setValue(typed);
                        }
                    }}
                    onBlur={() => setDraft(undefined)}
                />
                {isSet && <span className={css["ReductionField-underline"]} />}
            </span>
            {unit !== "" && (
                <span
                    className={classNames(
                        css["ReductionField-suffix"],
                        isSet && css["ReductionField-suffix--set"]
                    )}
                >
                    {unit}
                </span>
            )}
            <span
                className={classNames(
                    css["ReductionField-steppers"],
                    disabled && css["ReductionField-steppers--hidden"]
                )}
            >
                <button
                    className={css["ReductionField-stepper"]}
                    aria-label={`Increase ${ariaLabel}`}
                    onClick={() => step(stepSize)}
                >
                    <Icon icon={IconNames.CHEVRON_UP} size={12} />
                </button>
                <button
                    className={css["ReductionField-stepper"]}
                    aria-label={`Decrease ${ariaLabel}`}
                    onClick={() => step(-stepSize)}
                >
                    <Icon icon={IconNames.CHEVRON_DOWN} size={12} />
                </button>
            </span>
        </div>
    );
};
